import makeStyles from '@mui/styles/makeStyles';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RouteConfig, useNavigate, useQuery } from '../../../routes';
import routes, { order, payment, plan } from '../../routes';
import { fromLayout, fromOrder, fromPaymentMethod, fromSubscription, useAppDispatch } from '../../../store';
import { useLaunchDarkly } from '../../../launchdarkly';
import { PaymentMethodType, SubscriptionSource } from '../../../models';

const useStyles = makeStyles(theme => ({
  tabIcon: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  expandedTabIcon: {
    marginRight: theme.spacing(2),
  },
  selectedTabIcon: {
    color: '#FFFFFF',
  },
  defaultTabIcon: {
    color: '#9A9A9A',
  },
  tab: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
  },
  expandedTab: {
    justifyContent: 'flex-start',
    padding: theme.spacing(2, 4),
    borderBottom: '1px solid #E5E5E5',
  },
  defaultTab: {
    backgroundColor: '#FFFFFF !important',
    borderBottom: '1px solid #E5E5E5',
  },
  selectedTab: {
    backgroundColor: '#43509B !important',
    borderBottom: '1px solid #E5E5E5',
  },
}));

interface TabMenuProps {
  open: boolean;
  filteredRoutes: RouteConfig[];
}

const TabMenu = ({ open, filteredRoutes }: TabMenuProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { get } = useQuery();
  const { pathname } = useLocation();
  const { navigate, replaceQuery } = useNavigate();
  const { flags } = useLaunchDarkly();
  const accountId = Number(get('accountId'));
  const ftic = get('ftic') === 'true';
  const currentRoute = routes.find(route => pathname === route.path);
  const currentDefaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const hadActivePaidSubscription = useSelector(
    fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
  ).some(sub => new Date(sub.expiryDate).getTime() > Date.now());
  const hasRenewalPendingOrder = useSelector(fromOrder.selectPendingOrder).some(
    o => o.origin === 'RENEWAL',
  );
  const finalRoutes = filteredRoutes.filter(route => {
    if (!route.showInMenu) {
      return false;
    }
    if (!flags?.orderHistoryPageEnabled && route.path === order.path) {
      return false;
    }
    // Hide plan tab for AWS Marketplace users since they should use the dialog instead
    if (currentDefaultPaymentMethod === PaymentMethodType.AWS_MARKETPLACE
      && route.path === plan.path) {
      return false;
    }

    if (!hadActivePaidSubscription && flags?.truePlatformEnabled) {
      if (hasRenewalPendingOrder && route.path === payment.path) {
        return true;
      }
      return route.path.startsWith(plan.path);
    }

    return route.version === currentRoute?.version;
  });
  const renderTabIcon = (icon: ReactNode, isSelected: boolean) => (
    <span
      className={clsx(classes.tabIcon, {
        [classes.selectedTabIcon]: isSelected,
        [classes.defaultTabIcon]: !isSelected,
        [classes.expandedTabIcon]: open,
      })}
    >
      {icon}
    </span>
  );

  const renderCollapsedTab = (route: RouteConfig, isSelected: boolean) => (
    <Box display="flex" flexDirection="column" alignItems="center">
      {renderTabIcon(route.icon, isSelected)}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        fontSize={12}
        color={isSelected ? '#FFFFFF' : '#414141'}
      >
        <FormattedMessage id={`${route.pageTitle}.short`} />
      </Typography>
    </Box>
  );

  const renderExpandedTab = (route: RouteConfig, isSelected: boolean) => (
    <>
      {renderTabIcon(route.icon, isSelected)}
      <Typography
        variant="subtitle1"
        fontWeight={700}
        fontSize={14}
        textTransform="capitalize"
        color={isSelected ? '#FFFFFF' : '#414141'}
      >
        <FormattedMessage id={route.pageTitle} />
      </Typography>
    </>
  );

  return (
    <Box
      height="100%"
      sx={{
        padding: 0,
        backgroundColor: '#F8F8F8',
      }}
    >
      <List
        sx={{
          padding: 0,
        }}
      >
        {
          finalRoutes.map(route => {
            const isSelected = pathname.startsWith(route.path);
            return (
              <MenuItem
                key={route.path}
                selected
                onClick={() => {
                  if (currentRoute?.path === route.path) {
                    return;
                  }
                  dispatch(fromLayout.setLoading(true));
                  navigate(
                    route.path,
                    replaceQuery({ accountId, ftic }),
                  );
                }}
                className={clsx(classes.tab, {
                  [classes.selectedTab]: isSelected,
                  [classes.defaultTab]: !isSelected,
                  [classes.expandedTab]: open,
                })}
              >
                {open
                  ? renderExpandedTab(route, isSelected)
                  : renderCollapsedTab(route, isSelected)}
              </MenuItem>
            );
          })
        }
      </List>
    </Box>
  );
};

export default TabMenu;
